import { Column } from "@tanstack/react-table";
import { useEffect, useMemo } from "react";

import { BaseInput, Select } from "../input";
import { RadioSelect } from "../radio-select";
import { defaultFilters, TimeFilter, TimePeriod } from "../time-filter";

type FilterProps = {
  column: Column<any, unknown>;
  disabled?: boolean;
  clearRowSelection: () => void;
};

export const Filter = ({
  column,
  disabled,
  clearRowSelection,
}: FilterProps) => {
  const {
    type,
    title,
    name,
    placeholder,
    getItemLabel,
    fixedOptions,
    defaultOption,
    dateRangeExcludedOptions,
  } = column.columnDef.meta?.filter ?? {};
  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      type === "select"
        ? [
            ...new Set(
              Array.from(column.getFacetedUniqueValues().keys())
                .map((v) => v ?? "")
                .sort()
                .slice(0, 5000),
            ),
          ]
        : [],
    [column.getFacetedUniqueValues(), type],
  );

  const header = column.columnDef.header?.toString();

  // Update selected values when available options change due to other filters
  useEffect(() => {
    if (fixedOptions?.length) return;

    if (type === "select" && Array.isArray(columnFilterValue)) {
      const validValues = columnFilterValue.filter((value) =>
        sortedUniqueValues.includes(value),
      );

      if (validValues.length !== columnFilterValue.length) {
        column.setFilterValue(validValues);
      }
    }
  }, [sortedUniqueValues, columnFilterValue, type, column, fixedOptions]);

  useEffect(() => {
    if (column.columnDef.meta?.filter?.onFilterValueChanged) {
      column.columnDef.meta?.filter?.onFilterValueChanged(columnFilterValue);
    }
  }, [columnFilterValue]);

  switch (type) {
    case "select":
      return (
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ui">{title ?? header}</span>
          <Select
            id={column.id}
            name={name}
            title={title || header}
            key={column.id}
            options={
              fixedOptions?.length
                ? fixedOptions
                : sortedUniqueValues.map((o) => ({
                    value: o,
                    label: getItemLabel ? getItemLabel(o) : o,
                  }))
            }
            value={columnFilterValue}
            isMultiple
            maxItems={2}
            onChange={(v) => {
              clearRowSelection();
              column.setFilterValue(Array.isArray(v) ? v : [v]);
            }}
            dataTestId={`${column.id}-filter`}
            placeholder={placeholder || ""}
            disabled={disabled}
            compact
          />
        </div>
      );
    case "dateRange":
      return (
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ui">{title ?? header}</span>
          <TimeFilter
            defaultFilter={defaultOption as TimePeriod}
            filters={
              dateRangeExcludedOptions
                ? defaultFilters.filter(
                    (filter) => !dateRangeExcludedOptions.includes(filter),
                  )
                : defaultFilters
            }
            onChange={(period, range) => {
              clearRowSelection();
              column.setFilterValue({ period, range });
            }}
            filterStorageKey={`${column.id}-date-filter`}
            disabled={disabled}
            dataTestId={`${column.id}-filter`}
            unrestricted={column.columnDef.meta?.filter?.unrestricted}
            compact
          />
        </div>
      );
    case "radio": {
      const items =
        fixedOptions ??
        sortedUniqueValues.map((o) => ({
          value: o,
          label: getItemLabel ? getItemLabel(o) : o,
        }));

      return (
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ui">{title ?? header}</span>
          <RadioSelect
            items={items}
            defaultValue={defaultOption}
            value={columnFilterValue as string}
            onChange={(v) => {
              clearRowSelection();
              column.setFilterValue(v);
            }}
            disabled={disabled}
            dataTestId={`${column.id}-filter`}
            compact
          />
        </div>
      );
    }
    case "textInput":
      return (
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ui">{title ?? header}</span>
          <BaseInput
            className="w-[20rem]"
            id={column.id}
            name={name}
            placeholder={placeholder || ""}
            value={(columnFilterValue as string) ?? ""}
            onChange={(v) => {
              clearRowSelection();
              column.setFilterValue(v.target.value);
            }}
            disabled={disabled}
            dataTestId={`${column.id}-filter`}
            rightButtonIcon={columnFilterValue ? "regularXmark" : undefined}
            onRightButtonClick={() => {
              clearRowSelection();
              column.setFilterValue("");
            }}
            compact
          />
        </div>
      );
    default:
      return null;
  }
};
